'use client';
import classNames from 'classnames';
import React, { useEffect, useId, useRef } from 'react';
import { AsideModal, useAsideModalClose } from '../../components/AsideModal';
import Button from '../../components/Button';
import ChevronRight from '../../components/icons/ChevronRight';
import { SlideUpModal } from '../../components/SlideUpModal';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { GROUP_HEADING } from './FilterGroup';
import { TRANSLATIONS } from './i18n';
import { useProductEligibilityState } from './ProductEligibilityProvider';
import { useProductFilterState } from './ProductFilterProvider';
import { useProductSortConfig } from './ProductSortProvider';
import {
  getVisibleSortOptions,
  resolveDefaultSortOption,
} from './resolveActiveSortOption';
import { scrollToResults } from './scrollToResults';
import { SortByDropdown } from './SortByDropdown';
import { usePanelOverlay } from './SortFilterOverlayContext';
import {
  SortFilterTrigger,
  triggerWrapperClasses,
  type TriggerPlacement,
} from './SortFilterTrigger';
import { useProductSort } from './useProductSort';
import { YourDetailsGroup } from './YourDetailsGroup';

export type SortAndFilterPanelProps = {
  /** How the button that opens the overlay sits below the desktop breakpoint. */
  triggerPlacement?: TriggerPlacement;
  /** Filter groups render below the Sort By control and above Clear All. */
  children?: React.ReactNode;
  /** A sort option was selected. Receives the option's label, for analytics. */
  onSortChange?: (value: string, label: string) => void;
  /**
   * Clear All was used. A distinct analytics event from a sort selection, and
   * the two controls report separately (CMS-1915 #2532 / #2533), so the caller
   * is told which one the reader pressed.
   */
  onClearAll?: (source: 'header' | 'footer') => void;
  /** A citizenship was chosen in the "Your details" group (CMS-1905). */
  onCitizenshipChange?: (value: string) => void;
  /** A monthly income was entered, debounced rather than per keystroke. */
  onIncomeCommit?: (value: number | undefined) => void;
  /**
   * The reader opened the panel. Below `lg` the whole panel — and so both
   * "Your details" inputs — sits behind a button, so this is what separates
   * never opening it from opening it and not filling it in (Analytics 4).
   */
  onOpen?: () => void;
};

/**
 * Head / body / foot panel from the prototype. Sizes are plain utilities where a
 * token would fight it: no token carries 700, and the responsive padding tokens
 * narrow the panel on exactly the two viewports where it is an overlay.
 */
export const SortAndFilterPanel: React.FC<SortAndFilterPanelProps> = ({
  triggerPlacement = 'pinned',
  children,
  onSortChange,
  onClearAll,
  onCitizenshipChange,
  onIncomeCommit,
  onOpen,
}) => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });
  const sortLabelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  /**
   * What was applied when the reader started interacting, so a commit can be
   * compared against it. `activeOption` is no use for that: arrowing previews
   * through `setSortValue`, so by the time the commit lands it already reads as
   * the previewed value and every dismissal would look like a change.
   */
  const sortBeforePreview = useRef<string | undefined>(undefined);

  // Read only to pick an overlay after a click, never to decide first paint.
  // `useBreakpoint`'s 1280 step is `xxl`; its `lg` is 960, Tailwind's `lg:` 1280.
  const overlay = useBreakpoint<'sheet' | 'drawer' | null>({
    _: 'sheet',
    md: 'drawer',
    xxl: null,
  });

  // From the provider, not from a prop: the ordering reads the same config, and
  // two copies of it can disagree about which options exist — the control would
  // then offer a sort that ranks nothing.
  const sort = useProductSortConfig();
  // Editor-authored, falling back to the dictionary: CMS-1893 R1.4 makes both
  // labels the editor's, and R1.6 forbids ever rendering a blank heading.
  const title = sort?.componentTitle?.trim() || t.sort_and_filter_panel_title;
  const { activeOption, setSortValue } = useProductSort(sort);
  const options = getVisibleSortOptions(sort);

  // From the provider for the same reason as the sort config: the groups the
  // control resets have to be the groups the results filter by.
  const {
    categories,
    clearAll: clearFilters,
    activeCount,
    filterChanges,
  } = useProductFilterState();

  // Same reason as the sort config and the filter groups: the group the reader
  // types into has to be the one the loans are judged against.
  const { details, citizenshipOptions, incomeSupported, clearDetails } =
    useProductEligibilityState();
  const showYourDetails = citizenshipOptions.length > 0 || incomeSupported;

  // No sort options, no filter groups, and no eligibility check this market's
  // product data supports: nothing to render at all.
  const rendered =
    options.length > 0 || categories.length > 0 || showYourDetails;

  // Shared with the button on the loan bar, which opens the same overlay. Only a
  // floating panel reports itself to it: a pinned one keeps its own pill.
  const { isOpen, open, close, panelId, openerRef, hasBarButton } =
    usePanelOverlay(title, rendered && triggerPlacement === 'floating');

  // Reported when the overlay opens rather than on the pill's click: from `md`
  // to `lg` the loan bar's button opens it instead (CMS-2072), and a click
  // handler here would never hear of that.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) onOpen?.();
    wasOpen.current = isOpen;
  }, [isOpen, onOpen]);

  // The overlay locks the page: a change made in it scrolls once it has closed,
  // by any means. A tick undone before closing still counts (CMS-2073).
  const scrollWhenClosed = useRef(false);
  const scrollAfterChange = () => {
    if (isOpen) scrollWhenClosed.current = true;
    else scrollToResults();
  };
  // On the commit, so the scroll lands on the updated results. Reads the
  // `isOpen` of that render; it must not re-run when the overlay opens or closes.
  useEffect(() => {
    if (filterChanges > 0) scrollAfterChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChanges]);
  useEffect(() => {
    if (isOpen || !scrollWhenClosed.current) return;
    scrollWhenClosed.current = false;
    scrollToResults();
  }, [isOpen]);

  if (!rendered) return null;

  // Nothing to reset while sorting is the only control: it always holds a
  // value and defaults to Popular (req 15.3). The details are resettable in the
  // same sense the filters are — they start empty — so they bring it back.
  const showClearAll = categories.length > 0 || showYourDetails;

  // The label is the editor's in every vertical: a built-in one would have to
  // name a direction the measure does not own. Unlabelled options never arrive.
  const labelled = options.map((option) => ({
    value: option.value,
    label: option.label,
    // Carried through rather than dropped here: the dropdown renders it as the
    // option's second line (CMS-1893 R6.2).
    helpText: option.helpText,
  }));

  // Applied so the results reorder live, reporting nothing: the commit below
  // is the one reportable decision. Neither writes a history entry.
  const handlePreview = (value: string) => {
    if (sortBeforePreview.current === undefined) {
      sortBeforePreview.current = activeOption?.value ?? '';
    }
    setSortValue(value);
  };

  const handleChange = (value: string) => {
    const previous = sortBeforePreview.current ?? activeOption?.value;
    sortBeforePreview.current = undefined;
    setSortValue(value);
    // Re-picking the option already applied, or arrowing away and back before
    // dismissing, commits the same value it started from — no decision to
    // report.
    if (value === previous) return;
    scrollAfterChange();
    onSortChange?.(
      value,
      labelled.find((option) => option.value === value)?.label ?? value,
    );
  };

  // Its own analytics event, with a different action and filter_key.
  //
  // Clear All clears the filters and leaves the sort alone, on every vertical:
  // the sort always holds a value, so there is nothing to "clear" it to that
  // the reader did not already have.
  const handleClearAll = (source: 'header' | 'footer') => {
    // The control renders whenever the panel has any group, so it is pressable
    // with nothing to reset; reporting that would inflate the reset count this
    // ticket exists to produce. Income and citizenship reset with the filters
    // (AC 1f / 1g), and count as a reset in their own right: a reader who
    // entered only an income and pressed Clear All did change something.
    const resetsDetails = !!details.monthlyIncome || !!details.citizenship;
    const changed = activeCount > 0 || resetsDetails;

    clearFilters();
    clearDetails();
    if (changed) onClearAll?.(source);
  };

  const sortByLabel =
    sort?.sortButtonLabel?.trim() || t.sort_and_filter_sort_by;

  /**
   * `variant` keeps the label id unique: the CSS-hidden desktop column stays in
   * the tree, so an open overlay renders this a second time.
   */
  const card = (variant: PanelVariant) => {
    const isOverlay = variant !== 'desktop';
    return (
      <aside
        // Named here only outside an overlay: in the drawer and the sheet the
        // dialog around it carries the same name.
        aria-label={variant === 'desktop' ? title : undefined}
        // Desktop-only frame: below 1280 the panel fills a drawer or a sheet,
        // which supply their own edge.
        className="flex w-full flex-col bg-fill/card lg:rounded-radius/card lg:border lg:border-borders/default"
      >
        {/* In the drawer and the sheet the head stays put while the groups
          scroll, as the footer does: it carries the only way back. On desktop
          it scrolls with the rail, which sticks by its bottom edge with the
          footer's Clear All in view (CMS-2055 UI review); the rounded top
          keeps its fill inside the card's frame. */}
        <div
          data-component="sort-filter-panel-head"
          className={classNames(
            'flex items-center gap-2 border-b border-borders/default bg-fill/card p-4',
            isOverlay ? 'sticky top-0 z-10' : 'lg:rounded-t-radius/card',
          )}
        >
          {/* CMS-2072: the overlays close from a back arrow on the left, not
            a cross on the right. */}
          {isOverlay && (
            <BackButton
              label={t.sort_and_filter_close}
              inDrawer={variant === 'drawer'}
              closeSheet={close}
            />
          )}
          <p className={classNames('flex-1', HEADING_CLASSES[variant])}>
            {title}
          </p>
          {/* Behaviourally identical to the footer button (R8.2); only the
            analytics title differs. */}
          {showClearAll && (
            <button
              type="button"
              onClick={() => handleClearAll('header')}
              className={classNames(
                'mhg-alpha-typography-button-2 shrink-0 text-text/link',
                variant === 'sheet' && 'text-sm',
              )}
            >
              {t.sort_and_filter_clear_all}
            </button>
          )}
        </div>

        {/* Each section carries its own padding so the dividers run edge to edge,
          as in the prototype. */}
        <div className="flex flex-col divide-y divide-borders/default">
          {options.length > 0 && (
            <div className="flex flex-col gap-spacing/gap/y-xs p-4">
              <p
                id={`${sortLabelId}-${variant}`}
                className={GROUP_HEADING}
              >
                {sortByLabel}
              </p>
              <SortByDropdown
                options={labelled}
                value={activeOption?.value}
                ssrValue={resolveDefaultSortOption(sort)?.value}
                rendersOnServer={variant === 'desktop'}
                labelId={`${sortLabelId}-${variant}`}
                placeholder={sortByLabel}
                onChange={handleChange}
                onPreview={handlePreview}
              />
            </div>
          )}

          {/* Directly below Sort By and above the filter groups, per the Change 2
            prototype's revised placement (11 Aug). */}
          <YourDetailsGroup
            onCitizenshipChange={onCitizenshipChange}
            onIncomeCommit={onIncomeCommit}
          />

          {children}
        </div>

        {(showClearAll || isOverlay) && (
          <div
            data-component="sort-filter-panel-footer"
            // Pinned to the bottom of the overlay's own scroll. On desktop it
            // stays after the last category (CMS-2055 R3): the rail sticks by
            // its bottom edge instead, and the rounded bottom keeps the fill
            // inside the card's frame.
            className={classNames(
              'flex gap-3 border-t border-borders/default bg-fill/card p-4',
              isOverlay ? 'sticky bottom-0' : 'lg:rounded-b-radius/card',
            )}
          >
            {showClearAll && (
              <Button
                variant="outline"
                styleType="neutral"
                onClick={() => handleClearAll('footer')}
                className={FOOTER_BUTTON}
              >
                {t.sort_and_filter_clear_all}
              </Button>
            )}
            {/* R5.2: results already update behind the overlay, so Apply only
              dismisses it. */}
            {isOverlay && (
              <ApplyButton
                label={t.sort_and_filter_apply}
                inDrawer={variant === 'drawer'}
                closeSheet={close}
              />
            )}
          </div>
        )}
      </aside>
    );
  };

  return (
    <>
      {/* CSS-gated rather than hook-gated: a JS gate would serve the desktop
          column and swap in the trigger after hydration, a flash on every
          small screen. */}
      <div className="hidden lg:block">{card('desktop')}</div>

      <div
        data-component="sort-filter-trigger-box"
        className={triggerWrapperClasses(triggerPlacement, hasBarButton)}
      >
        <SortFilterTrigger
          buttonRef={triggerRef}
          label={title}
          controls={panelId}
          expanded={isOpen}
          onClick={() => open(triggerRef.current)}
          placement={triggerPlacement}
        />
      </div>

      {/* The panel fills the drawer edge to edge: padding out here would sit
          outside the divider lines and stop the head rule short. */}
      {isOpen && overlay === 'drawer' && (
        <AsideModal
          isOpen
          onClose={close}
          size="small"
          backgroundClassNames="md:!w-90 !bg-fill/card"
          // The card's head carries the back arrow.
          hideCloseButton
          dialog={{ label: title, id: panelId, returnFocusRef: openerRef }}
        >
          {card('drawer')}
        </AsideModal>
      )}

      {/* The card's head is the title bar, with the back arrow, so the sheet
          renders none; `!p-0` because its padding would double the card's. */}
      {isOpen && overlay === 'sheet' && (
        <SlideUpModal
          initialIsOpen
          onClose={close}
          hideCloseButton
          bodyClassName="!p-0"
          dialog={{ label: title, id: panelId, returnFocusRef: openerRef }}
        >
          {card('sheet')}
        </SlideUpModal>
      )}
    </>
  );
};

type PanelVariant = 'desktop' | 'drawer' | 'sheet';

/**
 * The head's title. Stepping up at `md:`, not down, on desktop: this config
 * generates no `max-*` variants, so `max-md:text-sm` would be dead CSS.
 */
const HEADING_CLASSES: Record<PanelVariant, string> = {
  desktop:
    'mhg-alpha-typography-body-2 font-bold text-sm text-text/primary md:text-base',
  drawer: 'mhg-alpha-typography-title-2 text-text/primary',
  sheet: 'mhg-alpha-typography-body-2 font-bold text-base text-text/primary',
};

type OverlayCloseProps = {
  label: string;
  inDrawer: boolean;
  closeSheet: () => void;
};

/**
 * Clear All and Apply share it so the pair always matches (CMS-2120): the
 * `button/small` type at the regular button's height. `size="small"` would
 * shrink the height as well, which the ticket rules out.
 */
const FOOTER_BUTTON = 'mhg-alpha-btn-small-text flex-1';

/** The drawer's close plays its slide-out; the sheet has none to play. */
const useOverlayClose = ({ inDrawer, closeSheet }: OverlayCloseProps) => {
  const closeDrawer = useAsideModalClose();
  return inDrawer ? closeDrawer : closeSheet;
};

/** Components so they can read the drawer's close from its context. */
const BackButton: React.FC<OverlayCloseProps> = (props) => {
  const onClose = useOverlayClose(props);
  return (
    <button
      type="button"
      aria-label={props.label}
      onClick={onClose}
      className="shrink-0 text-text/primary"
    >
      <ChevronRight className="size-5 rotate-180" />
    </button>
  );
};

const ApplyButton: React.FC<OverlayCloseProps> = (props) => {
  const onClose = useOverlayClose(props);
  return (
    // CMS-2072: the call to action of the overlay, so the CTA colour.
    <Button styleType="cta" onClick={onClose} className={FOOTER_BUTTON}>
      {props.label}
    </Button>
  );
};
